/**
 * Failure policy for every request that passes through the patched `window.fetch`.
 *
 * Two jobs, both of which have to happen at this seam rather than at call sites:
 *
 *  1. Give an unanswered request a deadline, so it fails instead of hanging forever.
 *  2. Replace the browser's own failure message with one that says which side broke. `fetch` rejects
 *     with a bare `TypeError: Failed to fetch` for a dropped connection, a DNS failure, a CORS refusal
 *     and a stopped server alike — and roughly fifty mutation hooks in this app render whatever they get
 *     via `toast.error(err.message)`. Left alone, a user whose Wi-Fi dropped and a user whose backend is
 *     down read the identical cryptic string, which is how an app ends up blaming the internet for its
 *     own outage.
 *
 * The distinction is drawn from connectivity, not from the error: no network → "no internet"; network
 * present but the request still failed → the server is the problem.
 */

import { getIsOnline, markOffline } from "@/lib/network-status";

export const NO_INTERNET_MESSAGE =
  "No internet connection. Please check your network and try again.";

export const SERVER_ISSUE_MESSAGE = "Application server issue. Please try again later.";

/** Applies to time-to-response-headers only — see the note on clearing the timer below. */
const DEFAULT_TIMEOUT_MS = 15_000;

/**
 * `RequestInit` plus an opt-in override. `fetch` ignores unknown properties, so this rides along
 * harmlessly on any request that does not care.
 */
export interface NetworkRequestInit extends RequestInit {
  /** Milliseconds before an unanswered request is aborted. `0` or `Infinity` removes the deadline. */
  timeoutMs?: number;
}

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === "AbortError";
}

/**
 * True for bodies that are still being SENT while we wait.
 *
 * A deadline on one of these measures the user's upload bandwidth, not the server's silence: the
 * FormData uploads in `api/assignments.ts`, `api/cv.ts` and `api/messages.ts` pass no signal of their
 * own, so a large attachment on a slow connection would be aborted mid-transfer and reported as a
 * server fault. Strings and URLSearchParams are small enough that sending time is not a factor.
 */
function isStreamingBody(body: BodyInit | null | undefined): boolean {
  if (body == null) return false;
  return !(typeof body === "string" || body instanceof URLSearchParams);
}

/**
 * Decides what a failed request should say. Synchronous and allocation-cheap by design: it adds nothing
 * to the time the user already spent waiting for the request that failed.
 *
 * Notably this does NOT ping to decide. A ping to our own API cannot separate "the user has no internet"
 * from "our server is down" — both fail the same way — so a failed ping is not evidence of either, and
 * treating it as evidence of the first would announce "No internet connection" to a user with working
 * Wi-Fi every time our own backend has an outage. The naive shape ("ping, then decide") also stacks a
 * second timeout on top of the request's own, so an offline user waits out both before seeing any
 * message. Both problems disappear by only claiming no-internet from the one signal that actually proves
 * it. See the header of `lib/network-status.ts` for the full reasoning and where a neutral third-party
 * probe would slot in if the captive-portal case ever needs distinguishing.
 */
function classifyFailure(): Error {
  // Already known offline, from the `offline` event or a failed recovery check. The retry loop in
  // network-status is already running, so nothing here needs to confirm it.
  if (!getIsOnline()) return new Error(NO_INTERNET_MESSAGE);

  // The OS sees no usable interface. The one signal reliable enough to act on alone.
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    markOffline();
    return new Error(NO_INTERNET_MESSAGE);
  }

  // Network present as far as anything can tell, yet the request still failed: attribute it to the
  // server rather than to the user's connection. Imprecise behind a captive portal, but wrong in the
  // safe direction — an app taking the blame for an ambiguous failure, instead of shifting it onto a
  // connection that is probably fine.
  return new Error(SERVER_ISSUE_MESSAGE);
}

/**
 * Runs one request under the policy above. Applied per attempt rather than around a whole retry
 * sequence, so a refresh-and-replay gets a fresh deadline instead of sharing one with the attempt that
 * already failed.
 */
export async function fetchWithNetworkPolicy(
  baseFetch: typeof fetch,
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const options = init as NetworkRequestInit | undefined;
  const configured = options?.timeoutMs;
  const budget = configured === undefined ? DEFAULT_TIMEOUT_MS : configured;

  // A signal from the caller means the caller is already managing this request's lifetime — cancelling
  // on unmount, or running a longer clock of its own (`api/cv-analysis.functions.ts` allows 30s per
  // attempt). Racing it with a second, shorter deadline would silently cut short a lifetime it owns.
  // A `Request` counts too: it always carries a signal of its own, and treating its cancellation as a
  // network fault would put an error toast on a request the caller deliberately dropped.
  const callerOwnsAbort = Boolean(options?.signal) || input instanceof Request;

  const skipDeadline =
    budget <= 0 || !Number.isFinite(budget) || callerOwnsAbort || isStreamingBody(options?.body);

  const controller = skipDeadline ? null : new AbortController();
  let timedOut = false;
  const timer =
    controller === null
      ? null
      : setTimeout(() => {
          timedOut = true;
          controller.abort();
        }, budget);

  try {
    return await baseFetch(
      input,
      controller === null ? init : { ...init, signal: controller.signal },
    );
  } catch (error) {
    // The caller cancelled deliberately. Reporting that as a server fault would put an error toast on
    // every navigation away from a pending request.
    if (!timedOut && callerOwnsAbort && isAbortError(error)) throw error;

    // Keep the real cause for debugging — the replacement below is written for users, and the original
    // is the only thing that distinguishes a CORS refusal from a dropped connection.
    console.error("Request failed:", input instanceof Request ? input.url : String(input), error);

    throw classifyFailure();
  } finally {
    // Cleared as soon as this promise settles — which `fetch` does when response HEADERS arrive, while
    // the body is still downloading. That is what keeps the deadline off large downloads: certificate
    // PDFs and Excel exports (`res.blob()` in `api/certificates.ts`, `api/students.ts`,
    // `api/tl-exams.ts`) can stream for minutes, and an armed timer would abort the response mid-read.
    if (timer !== null) clearTimeout(timer);
  }
}
